import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { getRouteApi, Link } from '@tanstack/react-router'
import { LibraryBig, RefreshCw } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { z } from 'zod'

import { assetsInfiniteQuery, type AssetFacets, type AssetListFilters } from '@/api/assets'
import { isApiError } from '@/api/client'
import { keys } from '@/api/keys'
import { Button } from '@/components/ui/button'
import { AssetGrid } from '@/features/library/AssetGrid'
import { AssetList } from '@/features/library/AssetList'
import { BulkActionBar } from '@/features/library/BulkActionBar'
import { FilterBar } from '@/features/library/FilterBar'
import { StoryShelf } from '@/features/library/StoryShelf'
import { useLibraryStore } from '@/stores/libraryStore'

/**
 * `/library?kind=&category=&world=&status=&tier=&q=&view=&sel=`
 *
 * Every param is optional and lenient (a bad value is dropped, never a
 * crash) so a stale bookmark still opens the library. Defaults are applied
 * by the consumer (`view ?? 'grid'`) so `<Link search={{}}>` stays terse.
 */
export const ASSET_KINDS = ['background', 'live_background', 'character', 'object', 'audio'] as const
export type AssetKind = (typeof ASSET_KINDS)[number]
/** `kind=story`: the made stories (thumbnail + length), not an asset kind (`StoryShelf`). */
export const STORY_KIND = 'story' as const
export const LIBRARY_KINDS = [...ASSET_KINDS, STORY_KIND] as const
export type LibraryKind = (typeof LIBRARY_KINDS)[number]

const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined)

export const librarySearchSchema = z.object({
  kind: lenient(z.enum(LIBRARY_KINDS)),
  category: lenient(z.string()),
  world: lenient(z.string()),
  status: lenient(z.enum(['ok', 'broken', 'missing', 'all'])),
  tier: lenient(z.enum(['auto', 'reviewed', 'none', 'any'])),
  q: lenient(z.string()),
  view: lenient(z.enum(['grid', 'list'])),
  /** Selected uid (verbatim; may contain `:` and `/`). */
  sel: lenient(z.string()),
})

export type LibrarySearch = z.infer<typeof librarySearchSchema>

export function validateLibrarySearch(input: Record<string, unknown>): LibrarySearch {
  return librarySearchSchema.parse(input)
}

/**
 * Route search → `GET /api/assets` query (§5.1).
 *
 * Exported because the search-param round-trip is the library's contract:
 * what the URL says is what the server is asked, and a test can check that
 * without rendering anything.
 */
export function searchToFilters(search: LibrarySearch): AssetListFilters {
  const filters: AssetListFilters = {}
  if (search.kind && search.kind !== STORY_KIND) filters.kind = search.kind
  if (search.category) filters.category = search.category
  if (search.world) filters.world = search.world
  if (search.status) filters.status = search.status
  if (search.tier) filters.tier = search.tier
  if (search.q) filters.q = search.q
  // Skins clutter the grid; a character's siblings live on its own page.
  if (search.kind === 'character') filters.skin = 'canonical'
  return filters
}

const EMPTY_FACETS: AssetFacets = { category: {}, world: {}, status: {}, review: {}, tier: {} }

const route = getRouteApi('/library')

export function LibraryPage() {
  const search = route.useSearch()
  const navigate = route.useNavigate()

  const setLastSearch = useLibraryStore((s) => s.setLastSearch)
  const clearSelection = useLibraryStore((s) => s.clearSelection)

  const filters = useMemo(() => searchToFilters(search), [search])
  // The Stories shelf reads `/api/stories`; the asset query waits until an asset kind is back.
  const stories = search.kind === STORY_KIND
  // `always`: the studio talks to its own server, so the browser's online flag is irrelevant — with
  // the default (`online`) a browser that thinks it is offline PAUSES the request and the page said
  // "Looking…" forever.
  const options = useMemo(() => ({ ...assetsInfiniteQuery(filters), networkMode: 'always' as const, enabled: !stories }), [filters, stories])
  const query = useInfiniteQuery(options)
  const slow = useSlowFlag(!stories && query.isPending && !query.isError, SLOW_MS)
  const qc = useQueryClient()
  // A plain refetch would join the stuck request (nothing loaded yet = no 'cancelRefetch'): drop it first.
  const retryStuck = async () => {
    await qc.cancelQueries({ queryKey: options.queryKey })
    await query.refetch()
  }

  useEffect(() => {
    setLastSearch(search)
  }, [search, setLastSearch])

  // A new filter is a new result set; keeping a selection across it would
  // let a bulk action hit assets that are no longer on screen.
  const filterKey = JSON.stringify(filters)
  useEffect(() => {
    clearSelection()
  }, [filterKey, clearSelection])

  const items = useMemo(() => query.data?.pages.flatMap((p) => p.items) ?? [], [query.data])
  const first = query.data?.pages[0]
  const facets = first?.facets ?? EMPTY_FACETS
  const total = first?.total

  const onChange = (patch: Partial<LibrarySearch>) => {
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })
  }

  const view = search.view ?? 'grid'

  return (
    <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-5 px-4 py-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-ink">Library</h1>
          <p className="text-sm text-ink-2">
            {stories ? 'Every story made so far — press one to watch it.' : 'Every background, plate, character, object and sound the studio knows about.'}
          </p>
        </div>
        <Button
          variant="ghost"
          onClick={() => void (stories ? qc.invalidateQueries({ queryKey: keys.stories.all() }) : query.isPending ? retryStuck() : query.refetch())}
          loading={!stories && query.isRefetching}
          aria-label="Reload the library"
        >
          <RefreshCw aria-hidden />
          Reload
        </Button>
      </header>

      <FilterBar search={search} facets={facets} onChange={onChange} total={stories ? undefined : total} loading={!stories && query.isPending} />

      {stories ? (
        <StoryShelf q={search.q} view={view} />
      ) : (
        <>
          {slow ? (
            <div className="card flex flex-wrap items-center gap-3 px-4 py-3 text-sm text-ink-2" role="status" data-testid="library-slow">
              <span className="flex-1">This is taking longer than usual — the studio server may be busy (indexing, or another big job).</span>
              <Button variant="secondary" size="sm" onClick={() => void retryStuck()}>
                Try again
              </Button>
            </div>
          ) : null}

          {query.isError ? (
            <ErrorPanel message={errorText(query.error)} onRetry={() => void query.refetch()} />
          ) : query.isPending ? (
            <SkeletonGrid />
          ) : items.length === 0 ? (
            <EmptyPanel hasFilters={Object.keys(filters).length > 0} onClear={() => onChange({ kind: undefined, category: undefined, world: undefined, status: undefined, tier: undefined, q: undefined })} />
          ) : view === 'list' ? (
            <AssetList
              items={items}
              hasMore={query.hasNextPage}
              loadingMore={query.isFetchingNextPage}
              onLoadMore={() => void query.fetchNextPage()}
            />
          ) : (
            <AssetGrid
              items={items}
              hasMore={query.hasNextPage}
              loadingMore={query.isFetchingNextPage}
              onLoadMore={() => void query.fetchNextPage()}
            />
          )}

          <BulkActionBar items={items} />
        </>
      )}
    </div>
  )
}

/** How long the first page may take before the page says so and offers a retry. */
export const SLOW_MS = 8_000

/** True once `active` has stayed true for `ms`. */
function useSlowFlag(active: boolean, ms: number): boolean {
  const [slow, setSlow] = useState(false)
  useEffect(() => {
    if (!active) {
      setSlow(false)
      return
    }
    const t = setTimeout(() => setSlow(true), ms)
    return () => clearTimeout(t)
  }, [active, ms])
  return slow
}

function errorText(error: unknown): string {
  if (isApiError(error) && error.code === 'network') return 'The studio server is not answering. Is the stack running?'
  return error instanceof Error ? error.message : String(error)
}

function ErrorPanel({ message, onRetry }: { message: string; onRetry(): void }) {
  return (
    <div className="card flex flex-col items-center gap-3 px-6 py-16 text-center" role="alert">
      <h2 className="text-lg font-semibold text-ink">The library could not load</h2>
      <p className="max-w-md text-sm text-ink-2">{message}</p>
      <Button variant="primary" onClick={onRetry}>
        Try again
      </Button>
    </div>
  )
}

function EmptyPanel({ hasFilters, onClear }: { hasFilters: boolean; onClear(): void }) {
  return (
    <div className="card flex flex-col items-center gap-3 px-6 py-16 text-center">
      <span className="inline-flex size-14 items-center justify-center rounded-2xl bg-paper-2 text-ink-2">
        <LibraryBig className="size-7" aria-hidden />
      </span>
      <h2 className="text-lg font-semibold text-ink">{hasFilters ? 'Nothing matches these filters' : 'The library is empty'}</h2>
      <p className="max-w-md text-sm text-ink-2">
        {hasFilters ? 'Try a wider search, or clear the filters to see everything.' : 'Index the bucket from Home and the assets will appear here.'}
      </p>
      {hasFilters ? (
        <Button variant="primary" onClick={onClear}>
          Clear filters
        </Button>
      ) : (
        <Button asChild variant="primary">
          <Link to="/">Go to Home</Link>
        </Button>
      )}
    </div>
  )
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4" aria-hidden>
      {Array.from({ length: 12 }, (_, i) => (
        <div key={i} className="card h-[268px] animate-pulse bg-paper-2" />
      ))}
    </div>
  )
}
